<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import {
  FILTERS_STATE_DEFAULT_VALUES,
  useKpisPerimeter,
  type FilterNameType,
  type FiltersTreeType
} from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import FiltersPopup from '#domain/core/filter/components/FiltersPopup.vue';
import { getPerimeterAvailableFilters } from '#domain/core/filter/filter.query';
import { getCheckedItems } from '#domain/core/filter/filter.utilities';
import type { FiltersStateType, PerimeterFilterViewType, ProductFilterConfigType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { unref, watch } from 'vue';

const { perimeterFilterView = undefined, productFilterConfig } = defineProps<{
  productFilterConfig: ProductFilterConfigType;
  perimeterFilterView?: PerimeterFilterViewType;
}>();

const {
  taxonomy: { taxonomy },
  filters: { selectedFiltersCount, selectedFiltersTree, updateSelectedFilters },
  isKpiFiltersPopinOpen
} = useKpisPerimeter();

const { t } = useLanguage();

const { data: availableFilters } = useQuery({
  queryKey: [getPerimeterAvailableFilters.name, perimeterFilterView, taxonomy],
  queryFn: () => getPerimeterAvailableFilters({ perimeterFilterView, taxonomy: unref(taxonomy) })
});

function openFiltersPopup() {
  isKpiFiltersPopinOpen.value = true;
}
function closeFiltersPopup() {
  isKpiFiltersPopinOpen.value = false;
}

function handleSelectFilters({ filtersState }: { filtersState: FiltersTreeType }) {
  const selectedFilters: FiltersStateType = { ...FILTERS_STATE_DEFAULT_VALUES };
  Object.keys(filtersState).forEach((key) => {
    const filterKey = key as FilterNameType;
    if (filtersState[filterKey]) {
      selectedFilters[filterKey] = Object.values(getCheckedItems(filtersState[filterKey])).map(({ nodeId }) => nodeId);
    }
  });

  updateSelectedFilters(selectedFilters);

  closeFiltersPopup();
}

watch(
  availableFilters,
  () => {
    if (!availableFilters.value) {
      return;
    }

    const selectedFilters: FiltersStateType = { ...FILTERS_STATE_DEFAULT_VALUES };
    Object.keys(selectedFiltersTree.value).forEach((key) => {
      const filterKey = key as FilterNameType;
      // Remove filters that are not available in current perimeter filter view
      if (!Object.keys(availableFilters.value).includes(key)) {
        selectedFilters[filterKey] = [];
      } else if (selectedFiltersTree.value[filterKey]) {
        selectedFilters[filterKey] = Object.values(getCheckedItems(selectedFiltersTree.value[filterKey])).map(
          ({ nodeId }) => nodeId
        );
      }
    });

    updateSelectedFilters(selectedFilters);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex-shrink-0">
    <ButtonBase
      icon-left="filter"
      variant="secondary"
      :rounded="true"
      :test-id="`button-modify-filters`"
      @click="openFiltersPopup"
    >
      <span class="font-bold">
        {{
          t('commonLabel.modifyFilters', {
            nbFiltersSelected: selectedFiltersCount
          })
        }}
      </span>
    </ButtonBase>
  </div>

  <KeepAlive>
    <FiltersPopup
      :perimeter-filter-view="perimeterFilterView"
      :show="isKpiFiltersPopinOpen"
      :selected-filters-tree="selectedFiltersTree"
      :product-filter-config="productFilterConfig"
      :taxonomy="taxonomy"
      @on-close="closeFiltersPopup"
      @on-select-filters="handleSelectFilters"
    />
  </KeepAlive>
</template>
